import * as React from "react";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { processTypeEnum } from "../../../Common/Resource/Contents";
import { CappedFlexiSection } from "../../../Common/SIPP/IncomePaymentDetails/CappedFlexiSection";
import { IncomeRequired } from "../../../Common/SIPP/IncomePaymentDetails/IncomeRequired";
import RegularIncomePayment from "../../../Common/SIPP/IncomePaymentDetails/RegularIncomePayment";
import {
    incomePaymentDetailsLabels,
    incomePaymentDetailsMessages,
} from "../../../Common/SIPP/IncomePaymentDetails/Resource/Content";
import { DrawdownIncomeReviewDetails } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownIncomeReviewDetails";
import { Arrangements } from "../../../EditIncomeWithdrawalsWizard/Actions/Arrangements";
import _ from "lodash";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { FlexibleIncome } from "./FlexibleIncome";
import { IncomePaymentDestination } from "../../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { PaymentMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/PaymentMethod";
import { CountryCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { FlexiModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/FlexiModel";
import { calculateNetDrawdownValue } from "../../../Common/SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { calculateMaximumIncomePaymentForFlexiTarget, frequencyMap } from "../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { ChooseAssetsToSellAssets } from "../../../Common/SIPP/DrawdownDetails/Store/DrawdownCalculationStoreStatic";

interface IOtherIncomePaymentDetailsProps extends IProcessComponentProps {
    data: DrawdownModel;
    drawdownIncomeReviewDetails: DrawdownIncomeReviewDetails;
    pensionBase: PensionBase;
    onIncomeReviewChange?: (v) => void;
    onAnnualIncomeChange?: (c,v) => void;
    onDrawdownIncomeAmendmentInstructionChange?: (v) => void;
    allBankAccounts?: BankAccount[];
    bankAccountInstructions?: BankAccountInstruction[];
    sellableAssets?: ChooseAssetsToSellAssets[];
}

interface IOtherIncomePaymentDetailsState { }

export class OtherIncomePaymentDetails extends ProcessComponent<
    IOtherIncomePaymentDetailsProps,
    IOtherIncomePaymentDetailsState
> {
    constructor(props: any) {
        super(props);
    }

    private getIncomeProps = (data, onChange) => {
        return {
            data: data?.Income,
            onChange: (changes) => this.onDataChange(onChange, changes, data, "Income"),
            onRef: (ref) => this.appendChild(ref),
            showErrorMsgPopUp: this.props.showErrorMsgPopUp,
            isReview: this.props.isReview,
        };
    };

    private getAnnualIncomeData() {
        const { data, pensionBase } = this.props;

        if (pensionBase.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income.TargetSpecificArrangements === true
            || (pensionBase.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.GrossIncomePerPayment?.Amount ?? 0) > 0
        ) {
            return pensionBase.DrawdownIncomeAmendmentModel.DrawdownIncomeAmendmentInstruction.Income
        }

        return data?.Income;
    }

    private mapDrawdownIncomeToFlexiModel()
    {
        const { data, pensionBase } = this.props;

        if (data?.DrawdownType === DrawdownType.Flexible) {
            const drawdownModel = data;

            const drawdownIncome: FlexiModel = {
                PensionArrangementId: null,
                From: "Crystallisation",
                Amount: calculateNetDrawdownValue(data, pensionBase?.PensionProtection),
                Type: "Member",
                IncomePerPayment: (drawdownModel?.Income?.AnnualAmount?.Amount?.Amount || 0) / frequencyMap[drawdownModel?.Income?.Frequency || Frequency.Monthly],
                MaximumIncomePerPayment: calculateMaximumIncomePaymentForFlexiTarget(processTypeEnum.TakePensionBenefits, null, data, true)
            };

            return drawdownIncome;
        }

        return null;
    }
    
    public render(): JSX.Element {
        const { data, onChange, pensionBase, drawdownIncomeReviewDetails, sellableAssets} = this.props;
        
        const drawdownType = data?.DrawdownType;
        const existingArrangements:Arrangements = {
            CappedModels: pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements || [],
            FlexiModels: pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements || []
        }

        const bankAccountDestinations = data?.Income?.IncomePaymentAllocations?.filter(alloc => alloc.Destination == IncomePaymentDestination.BankAccount).map(x => x.BankAccountId) ?? [];
        const hasInternationalBankAccountDestination = bankAccountDestinations?.some(x => {
            var allBankAccounts = this.props.allBankAccounts ?? [];
            const bankAccount = allBankAccounts.find((ba) => ba.BankAccountId === x);
            return bankAccount && bankAccount.BankCountry !== CountryCode.GBR;
        });
            
        return (
            <>
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={incomePaymentDetailsLabels.needToProvideAnIncome}
                    selected={data?.Income?.TaxableIncomeRequiredInAddition || pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.TaxableIncomeRequiredInAddition || false}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    onChange={(v) => {
                        this.onDataChange(
                            (changes) => this.onDataChange(onChange, changes, data, "Income"),
                            { TaxableIncomeRequiredInAddition: v },
                            data.Income
                        );
                        if (!v) {
                            this.onDataChange(
                                (changes) => this.onDataChange(onChange, changes, data, "Income"),
                                { IncomeDisinvestmentAllocations: [], TaxableIncomeRequiredInAddition: v },
                                data.Income
                            );
                        }
                    }}
                />
                {(data?.Income?.TaxableIncomeRequiredInAddition || pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.TaxableIncomeRequiredInAddition) && (
                    <>
                        <IncomeRequired
                            data={this.getAnnualIncomeData()}
                            onChange={(changes) => {

                                var clonedContribution = _.cloneDeep(pensionBase)
                                var drawdownModelAnnualIncome: DrawdownAnnualIncome = null;
                                var amendmendModelAnnualIncome: DrawdownAnnualIncome = null;
                                
                                if(clonedContribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income)
                                {
                                    this.onDataChange(
                                        (v) => { amendmendModelAnnualIncome= v.Income },
                                        changes,
                                        clonedContribution.DrawdownIncomeAmendmentModel.DrawdownIncomeAmendmentInstruction,
                                        "Income"
                                    )
                                }

                                if(clonedContribution?.DrawdownModel?.Income)
                                {
                                    this.onDataChange(
                                        (v) => { drawdownModelAnnualIncome = v.Income },
                                        changes,
                                        clonedContribution?.DrawdownModel,
                                        "Income"
                                    )
                                }

                                this.props.onAnnualIncomeChange(drawdownModelAnnualIncome,amendmendModelAnnualIncome);
                            }}
                            onRef={(ref) => this.appendChild(ref)}
                            labels={incomePaymentDetailsLabels}
                            messages={incomePaymentDetailsMessages}
                            drawdownType={drawdownType}
                            hasCappedArrangement={!isNullOrEmpty(existingArrangements?.CappedModels)}
                            pensionBase={pensionBase}
                            isITFC={false}
                            processType={processTypeEnum.TakePensionBenefits}
                        />
                        { data?.Income?.TargetSpecificArrangements && (
                            <CappedFlexiSection
                                showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                                onRef={(ref) => this.appendChild(ref)}
                                income={data?.Income}
                                onIncomeChange={(changes) => this.onDataChange(onChange, changes, data, "Income")}
                                data={existingArrangements}
                                onChange={(changes, type) =>    {
                                    const convertedChanges =  type === "CappedModels" ? { CappedArrangements: changes?.CappedModels } : {FlexiArrangements: changes?.FlexiModels } ;
                                    this.onDataChange(
                                        (changes) => this.props.onDrawdownIncomeAmendmentInstructionChange(changes),
                                        convertedChanges,
                                        pensionBase?.DrawdownIncomeAmendmentModel,
                                        'DrawdownIncomeAmendmentInstruction'
                                    );
                                }}
                                drawdownType={drawdownType}
                                pensionProtection={pensionBase?.PensionProtection}
                                drawdownIncomeReview={drawdownIncomeReviewDetails}
                                modelData={pensionBase?.DrawdownModel}
                                onIncomeReviewChange={(changes) => {
                                   this.props.onIncomeReviewChange(changes)
                                }}
                                drawdownIncomeFlexiModel={this.mapDrawdownIncomeToFlexiModel()}
                                processType={processTypeEnum.TakePensionBenefits}
                            />
                        )}
                        <RegularIncomePayment
                            {...this.getIncomeProps(data, onChange)}
                            labels={incomePaymentDetailsLabels}
                            messages={incomePaymentDetailsMessages}
                            drawdownType={drawdownType}
                            pensionBase={pensionBase}
                            shouldHidePaymentMethod={bankAccountDestinations.length == 0}
                            hasInternationalBankAccountDestination={hasInternationalBankAccountDestination}
                            processType={processTypeEnum.TakePensionBenefits}
                            isITFC={false}
                            subaccountHierarchyid={getQueryParameters()["subaccounthierarchyid"]}
                            availableCash={data?.CrystallisationDetails?.AvailableCash?.Amount}
                            taxFreeLumpSum={data?.TaxFreeLumpSum?.Amount}
                            sellableAssets={sellableAssets}
                        />
                        {/* {!data?.Income?.TargetSpecificArrangements &&
                            <FlexibleIncome
                                {...this.getIncomeProps(data, onChange)}
                                drawdownType={drawdownType}
                                pensionBase={pensionBase}
                                processType={processTypeEnum.TakePensionBenefits}
                                onIncomeReviewChange={(changes) => {
                                    this.props.onIncomeReviewChange(changes)
                                }}
                            />
                        } */}
                    </>
                )}
            </>
        );
    }
}
